import { useState } from "react"
import useModals from "./useModals"
import { pluralize } from "web/services/inflector"
import { Button } from "web/components"
import classNames from "classnames"
import routes from "web/routes/constants"

function DuplicateCompanyModal({
  close,
  respond,
  createNewCompany,
  selectExistingCompany,
  duplicateCompanies,
  name,
  closeParentModal,
  multipleValues,
  selectedValues,
}) {
  const buttonText = selectExistingCompany ? "Use Selected" : "Add Company"
  const companyLabel = selectExistingCompany ? `${name} (Add)` : name
  const newCompany = {
    type: "company",
    value: "new",
    label: companyLabel,
  }
  const companies = selectExistingCompany
    ? [newCompany, ...duplicateCompanies]
    : duplicateCompanies

  const [selectedCompany, setSelectedCompany] = useState(companies[0])

  const multiValueSelection = !!selectedValues
    ? removeDuplicatesByLabel([...selectedValues, selectedCompany])
    : [selectedCompany]

  async function handleAdd() {
    if (selectExistingCompany && selectedCompany.value !== "new") {
      if (multipleValues) {
        await selectExistingCompany(multiValueSelection)
      } else {
        await selectExistingCompany(selectedCompany)
      }
      close()
    } else {
      const newCompany = await createNewCompany(name)
      respond(newCompany)
    }
  }

  function RadioLabel({ company }) {
    return (
      <>
        <input
          type="radio"
          className="mr-3"
          name="company"
          value={company.value}
          onChange={() => {
            setSelectedCompany(company)
          }}
          checked={company.value === selectedCompany.value}
        />
        {company.label}
      </>
    )
  }

  function LinkLabel({ company }) {
    return (
      <a
        href={routes.tlo("companies", "all", "", company.value)}
        className="text-blue-500 hover:text-blue-600"
        onClick={() => {
          close()
          if (closeParentModal) {
            closeParentModal()
          }
        }}
        target="_self"
      >
        {company.label}
      </a>
    )
  }

  function removeDuplicatesByLabel(companies) {
    const selections = new Set()
    return companies.filter((company) => {
      if (selections.has(company.label)) {
        return false
      }
      selections.add(company.label)
      return true
    })
  }

  return (
    <>
      <div className="modal-content-above-commands dialog-prompt">
        <div>
          <div>
            The company you are trying to add may already exist. Possible
            matches for <span className="font-bold">{name}</span>:
          </div>
          <ul
            className={classNames("mt-2", {
              "ml-6 list-disc": !selectExistingCompany,
              "flex flex-col space-y-1": selectExistingCompany,
            })}
          >
            {companies.map((company, index) => (
              <li key={index}>
                <label>
                  {selectExistingCompany ? (
                    <RadioLabel company={company} />
                  ) : (
                    <LinkLabel company={company} close={close} />
                  )}
                </label>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="modal-commands">
        <div className="right-buttons">
          <Button onClick={handleAdd}>{buttonText}</Button>
        </div>
      </div>
    </>
  )
}

export default function useDuplicateCompanyModal({
  createNewCompany,
  selectExistingCompany,
  closeParentModal,
  multipleValues,
  selectedValues,
}) {
  const { showModal } = useModals()

  return async function ({ name, searchResults }) {
    const title = `Potential Duplicate ${pluralize(
      "Company",
      searchResults.length
    )} Found`

    return await showModal({
      title,
      fullScreen: false,
      heightPixels: 500,
      widthPixels: 600,
      component: ({ close, respond }) => (
        <DuplicateCompanyModal
          close={close}
          respond={respond}
          createNewCompany={createNewCompany}
          selectExistingCompany={selectExistingCompany}
          duplicateCompanies={searchResults}
          name={name}
          closeParentModal={closeParentModal}
          multipleValues={multipleValues}
          selectedValues={selectedValues}
        />
      ),
    })
  }
}
